import React, { useCallback } from "react";
import { Container, Col, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import withStyles from "isomorphic-style-loader/withStyles";
import "@fortawesome/fontawesome-free/css/all.min.css";
import styles from "./styles.scss";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import { get } from "lodash";
import OnboardingCard from "./common/onboardingCard";
import { useDomainProgress } from "../../onboardingSteps/domainConfiguration/hooks/useDomainProgress";
import { useDnsDelegateDeliveryStatus } from "../../onboardingSteps/domainConfiguration/hooks/useDnsDelegateDeliveryStatus";
import ICharityEmailSetup from "../../../../../types/ICharityEmailSetup";
import { usePaymentProgress } from "../../onboardingSteps/paymentSetup/hooks/usePaymentProgress";
import { useOrganizationSetupProgress } from "../../onboardingSteps/organizationSetup/hooks/useOrganizationSetupProgress";
import { useBrandSetupProgress } from "../../onboardingSteps/brandSetup/hooks/useBrandSetupProgress";
import { useMmsProgress } from "../../onboardingSteps/mmsSetup/hooks/useMmsProgress";
import { useMmsAttentionStatus } from "../../onboardingSteps/mmsSetup/hooks/useMmsAttentionStatus";
import { BrandSetupData } from "../../../../../utils/firebase/brandSetup";
import { PaymentSetupData } from "../../../../../utils/firebase/paymentSetup";
import { ICharityMmsSetup } from "../../../../../utils/firebase/mmsSetup";
import {
  getSetupCardLockState,
  hasWebsitePublished,
  isCharityFirebasePathLoaded,
  isOrganizationSetupComplete,
  SetupCardKey,
} from "../../../../../utils/onboarding/dashboardSequencing";
import { ONBOARDING_DASHBOARD_PATH } from "../../../../../utils/onboarding/stepNavigation";
import { OnboardingDisplayState } from "./types";
import OnboardingDashboardLoader from "./onboardingDashboardLoader";
import { Navigate } from "react-router-dom";

interface Props extends ServiceCallerProps {
  charityId: string;
  onboardingRequired: boolean;
  onboardingRequiredLoaded: boolean;
  emailSetup: ICharityEmailSetup | null;
  emailSetupLoaded: boolean;
  mmsSetup: ICharityMmsSetup | null;
  mmsSetupLoaded: boolean;
  paymentSetupData: PaymentSetupData | null;
  paymentSetupLoaded: boolean;
  organizationProgressData: any;
  organizationDataLoaded: boolean;
  brandSetupData: BrandSetupData | null;
  brandSetupDataLoaded: boolean;
}

const OnboardingPage: React.FC<Props> = ({
  charityId,
  onboardingRequired,
  onboardingRequiredLoaded,
  emailSetup,
  emailSetupLoaded,
  mmsSetup,
  mmsSetupLoaded,
  paymentSetupData,
  paymentSetupLoaded,
  organizationProgressData,
  organizationDataLoaded,
  brandSetupData,
  brandSetupDataLoaded,
  config,
  serviceAccessToken,
}) => {
  const navigate = useNavigate();

  const domainProgress = useDomainProgress(charityId, emailSetup, emailSetupLoaded);

  const dnsDelegateDeliveryStatus = useDnsDelegateDeliveryStatus({
    charityId,
    config,
    serviceAccessToken,
    emailSetup,
    emailSetupLoaded,
  });

  const mmsAttentionStatus = useMmsAttentionStatus({
    charityId,
    config,
    serviceAccessToken,
    mmsSetup,
    mmsSetupLoaded,
  });

  const mmsProgress = useMmsProgress(
    charityId,
    mmsSetup,
    mmsSetupLoaded,
    mmsAttentionStatus.needsAttention,
    mmsAttentionStatus.pipelineRunning,
    mmsAttentionStatus.statusError,
    mmsAttentionStatus.setupFailed
  );

  const paymentProgress = usePaymentProgress(charityId, paymentSetupData, paymentSetupLoaded);

  const organizationProgress = useOrganizationSetupProgress(
    charityId,
    organizationProgressData,
    organizationDataLoaded
  );

  const brandSetupProgress = useBrandSetupProgress(
    charityId,
    brandSetupData,
    brandSetupDataLoaded,
    paymentSetupData,
    paymentSetupLoaded
  );

  const orgComplete = isOrganizationSetupComplete(
    organizationProgressData,
    organizationDataLoaded
  );
  const published = hasWebsitePublished(brandSetupData);

  const lockFor = (key: SetupCardKey) =>
    getSetupCardLockState(key, orgComplete, published);

  type SetupTask = {
    key: SetupCardKey;
    title: string;
    completedSteps: number;
    totalSteps: number;
    color: string;
    route: string;
    displayState?: OnboardingDisplayState;
    needsAttention?: boolean;
  };

  const organizationTask: SetupTask = {
    key: "organization",
    title: "Organization Data",
    completedSteps: organizationProgress.completedSteps,
    totalSteps: organizationProgress.totalSteps,
    color: "#0078D4",
    route: `${ONBOARDING_DASHBOARD_PATH}/organization`,
  };

  const brandingTask: SetupTask = {
    key: "branding",
    title: "Customize Website",
    completedSteps: brandSetupProgress.completedSteps,
    totalSteps: brandSetupProgress.totalSteps,
    color: "#FBBF24",
    route: `${ONBOARDING_DASHBOARD_PATH}/branding`,
  };

  const givesparkTask: SetupTask = {
    key: "givespark",
    title: "GiveSpark",
    completedSteps: paymentProgress.completedSteps,
    totalSteps: paymentProgress.totalSteps,
    color: "#24D164",
    route: `${ONBOARDING_DASHBOARD_PATH}/donations`,
  };

  const emailTask: SetupTask = {
    key: "email",
    title: "Email Communications",
    completedSteps: domainProgress.completedSteps,
    totalSteps: domainProgress.totalSteps,
    color: "#F472B6",
    route: `${ONBOARDING_DASHBOARD_PATH}/email-setup`,
    displayState: dnsDelegateDeliveryStatus.deliveryFailed ? "attention" : undefined,
    needsAttention: dnsDelegateDeliveryStatus.deliveryFailed,
  };

  const mmsTask: SetupTask = {
    key: "mms",
    title: "MMS Communications",
    completedSteps: mmsProgress.completedSteps,
    totalSteps: mmsProgress.totalSteps,
    color: "#8B5CF6",
    route: `${ONBOARDING_DASHBOARD_PATH}/mms-setup`,
    displayState: mmsProgress.displayState,
    needsAttention: mmsProgress.needsAttention,
  };

  const setupSections = [
    {
      title: "Set Up Your Charity",
      tasks: [organizationTask, brandingTask],
    },
    {
      title: "Set Up GiveSpark",
      tasks: [givesparkTask],
    },
    {
      title: "Set Up Your Ways to Connect",
      tasks: [emailTask, mmsTask],
    },
  ];

  const handleTaskClick = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  const dashboardDataLoaded =
    onboardingRequiredLoaded &&
    emailSetupLoaded &&
    mmsSetupLoaded &&
    paymentSetupLoaded &&
    organizationDataLoaded &&
    brandSetupDataLoaded;

  if (!dashboardDataLoaded) {
    return <OnboardingDashboardLoader />;
  }

  if (!onboardingRequired) {
    return <Navigate to="/contacts" replace />;
  }

  return (
    <Container className={styles.root}>
      <Row className={styles.mainContainer}>
        <Col xs={12}>
          <div className={styles.dashboardSections}>
            {setupSections.map(section => (
              <section key={section.title} className={styles.setupSectionPanel}>
                <div className={styles.sectionHeader}>
                  <h6 className={styles.sectionTitle}>{section.title}</h6>
                </div>

                <div className={styles.cardsStrip}>
                  {section.tasks.map(task => {
                    const lock = lockFor(task.key);
                    return (
                      <div key={task.key} className={styles.cardSlot}>
                        <OnboardingCard
                          cardKey={task.key}
                          title={task.title}
                          completedSteps={task.completedSteps}
                          totalSteps={task.totalSteps}
                          color={task.color}
                          displayState={task.displayState}
                          needsAttention={task.needsAttention}
                          locked={lock.locked}
                          lockTooltip={lock.tooltip}
                          onClick={
                            lock.locked
                              ? undefined
                              : () => handleTaskClick(task.route)
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const { user, firebase } = state;
  const charityId = user.currentCharity?.id || "";

  const emailSetupLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/email"
  );

  const emailSetup: ICharityEmailSetup | null = emailSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.email`, null)
    : null;

  const mmsSetupLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/mms"
  );

  const mmsSetup: ICharityMmsSetup | null = mmsSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.mms`, null)
    : null;

  const paymentSetupLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/payment"
  );

  const paymentSetupData: PaymentSetupData | null = paymentSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.payment`, null)
    : null;

  const organizationDataLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/organization"
  );

  const organizationProgressData = organizationDataLoaded
    ? get(firebase, `data.${charityId}.charitySetup.organization`, null)
    : null;

  const brandSetupDataLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "charitySetup/brandSetup"
  );

  const brandSetupData = brandSetupDataLoaded
    ? get(firebase, `data.${charityId}.charitySetup.brandSetup`, null)
    : null;

  const onboardingRequiredLoaded = isCharityFirebasePathLoaded(
    firebase,
    charityId,
    "onboardingRequired"
  );

  const onboardingRequired = onboardingRequiredLoaded
    ? get(firebase, `data.${charityId}.onboardingRequired`) === true
    : false;

  return {
    charityId,
    onboardingRequired,
    onboardingRequiredLoaded,
    emailSetup,
    emailSetupLoaded,
    mmsSetup,
    mmsSetupLoaded,
    paymentSetupData,
    paymentSetupLoaded,
    organizationProgressData,
    organizationDataLoaded,
    brandSetupData,
    brandSetupDataLoaded,
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((props: any, store: any) => {
    const state = store.getState();
    const charityId = state.user.currentCharity?.id;
    if (!charityId) {
      return [];
    }
    return [
      `${charityId}/charitySetup/email`,
      `${charityId}/charitySetup/mms`,
      `${charityId}/charitySetup/payment`,
      `${charityId}/charitySetup/organization`,
      `${charityId}/charitySetup/brandSetup`,
      `${charityId}/onboardingRequired`,
    ];
  })(serviceCaller(withStyles(styles)(OnboardingPage)))
);
